/**
 * SectionChangeHistory — collapsible "Change History" panel used by the
 * Account Info, Contact & Address, and FATCA/CRS tabs on the Investor
 * Account details page.
 *
 * Matches the visual + interaction pattern of the Bank & Demat and
 * Nomination change-history displays: click the History icon to expand,
 * see one card per revision with actor + timestamp + optional reason,
 * plus a two-column red-strike-through / green-new diff table.
 *
 * Values are flattened for readability — nested address / FATCA blocks
 * are broken into per-sub-field rows so a layperson doesn't have to
 * parse JSON braces. Arrays of objects (e.g. primary_contacts) show
 * one row per element with its own sub-field diff.
 */
import React, { useState } from 'react';
import { History as HistoryIcon, ChevronDown, ChevronRight } from 'lucide-react';

// snake_case / kebab → Title Case (with the odd acronym kept UPPER).
const humanizeKey = (k) => {
  if (!k) return '';
  const parts = String(k).replace(/[-_]+/g, ' ').split(' ');
  return parts
    .map((p) => {
      const upper = p.toUpperCase();
      if (['PAN', 'TIN', 'GIIN', 'US', 'DOB', 'DOI', 'ID', 'IFSC', 'MICR', 'ISIN', 'DP'].includes(upper)) return upper;
      return p.charAt(0).toUpperCase() + p.slice(1).toLowerCase();
    })
    .join(' ');
};

// Format a primitive value for display. Empty / null / undefined → "—".
const fmtPrimitive = (v) => {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  const s = String(v).trim();
  return s === '' ? '—' : s;
};

// True for {} — plain objects only, NOT arrays / dates / null.
const isPlainObject = (v) =>
  v !== null && typeof v === 'object' && !Array.isArray(v) && v.constructor === Object;

// Deep equality good enough for the value types we get from Mongo
// (primitives, plain objects, arrays of the same).
const deepEqual = (a, b) => {
  if (a === b) return true;
  if (a == null || b == null) return a == null && b == null;
  if (typeof a !== typeof b) return false;
  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) return false;
    return a.every((x, i) => deepEqual(x, b[i]));
  }
  if (isPlainObject(a)) {
    if (!isPlainObject(b)) return false;
    const ka = Object.keys(a);
    const kb = Object.keys(b);
    if (ka.length !== kb.length) return false;
    return ka.every((k) => deepEqual(a[k], b[k]));
  }
  return false;
};

/**
 * Recursively walk two values and yield {label, old, new} diff rows.
 * Keys are joined with " · " so a change deep in a nested object
 * reads like "Correspondence Address · City".
 *
 * Arrays of scalars are joined with commas; arrays of objects are
 * walked index-by-index with the label suffixed by "#1", "#2", ...
 */
function* flattenDiff(label, oldV, newV) {
  if (deepEqual(oldV, newV)) return;

  // Plain object on either side → walk union of keys.
  if (isPlainObject(oldV) || isPlainObject(newV)) {
    const a = isPlainObject(oldV) ? oldV : {};
    const b = isPlainObject(newV) ? newV : {};
    const keys = Array.from(new Set([...Object.keys(a), ...Object.keys(b)]));
    for (const k of keys) {
      const childLabel = label ? `${label} · ${humanizeKey(k)}` : humanizeKey(k);
      yield* flattenDiff(childLabel, a[k], b[k]);
    }
    return;
  }

  // Array on either side.
  if (Array.isArray(oldV) || Array.isArray(newV)) {
    const a = Array.isArray(oldV) ? oldV : [];
    const b = Array.isArray(newV) ? newV : [];
    // Arrays of plain objects → walk index-by-index.
    const containsObj =
      a.some((x) => isPlainObject(x)) || b.some((x) => isPlainObject(x));
    if (containsObj) {
      const max = Math.max(a.length, b.length);
      for (let i = 0; i < max; i++) {
        yield* flattenDiff(`${label} #${i + 1}`, a[i], b[i]);
      }
    } else {
      // Arrays of scalars → single row with comma-joined values.
      const joinedA = a.map(fmtPrimitive).filter((s) => s !== '—').join(', ') || '—';
      const joinedB = b.map(fmtPrimitive).filter((s) => s !== '—').join(', ') || '—';
      if (joinedA !== joinedB) {
        yield { label, old: joinedA, new: joinedB };
      }
    }
    return;
  }

  // Primitive.
  yield { label, old: fmtPrimitive(oldV), new: fmtPrimitive(newV) };
}

const SectionChangeHistory = ({ entries, testIdPrefix = 'section-history' }) => {
  const [open, setOpen] = useState(false);
  const list = Array.isArray(entries) ? entries : [];
  if (list.length === 0) return null;

  return (
    <div className="mt-6 border-t pt-4" data-testid={`${testIdPrefix}-panel`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-blue-700"
        data-testid={`${testIdPrefix}-toggle`}
      >
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <HistoryIcon size={16} />
        Change History ({list.length})
      </button>
      {open && (
        <div className="mt-3 space-y-2" data-testid={`${testIdPrefix}-list`}>
          {[...list].reverse().map((h, i) => {
            const prev = h.previous || {};
            const next = h.new || {};
            const topKeys = Array.from(new Set([...Object.keys(prev), ...Object.keys(next)]));
            const rows = [];
            for (const k of topKeys) {
              for (const row of flattenDiff(humanizeKey(k), prev[k], next[k])) {
                rows.push(row);
              }
            }
            return (
              <div key={i} className="bg-gray-50 border rounded p-3" data-testid={`${testIdPrefix}-entry-${i}`}>
                <p className="text-xs text-gray-700">
                  <span className="font-medium">{h.changed_by}</span>
                  <span className="text-gray-400 ml-1">on {new Date(h.changed_at).toLocaleString()}</span>
                </p>
                {h.change_remarks && (
                  <p className="text-xs text-amber-700 italic mt-1" data-testid={`${testIdPrefix}-remark-${i}`}>
                    Reason: {h.change_remarks}
                  </p>
                )}
                {rows.length === 0 ? (
                  <p className="text-[11px] text-gray-400 italic mt-1">No field-level changes detected</p>
                ) : (
                  <table className="w-full text-[11px] mt-2">
                    <thead>
                      <tr className="text-gray-500">
                        <th className="text-left font-medium pr-2 w-40">Field</th>
                        <th className="text-left font-medium pr-2">Old Value</th>
                        <th className="text-left font-medium">New Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((d, idx) => (
                        <tr key={idx} className="align-top">
                          <td className="pr-2 py-0.5 text-gray-600 whitespace-nowrap">{d.label}</td>
                          <td className="pr-2 py-0.5 line-through text-red-600 break-words">{d.old}</td>
                          <td className="py-0.5 text-green-700 break-words">{d.new}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SectionChangeHistory;
